import React from 'react';
import { useTranslation } from 'react-i18next';
import { Icons } from '../../shared/ui';
const { Alert: ExclamationTriangleIcon, XCircle: XCircleIcon, Refresh: ArrowPathIcon, Home: HomeIcon, Wifi: WifiIcon } = Icons;

// Helper function to safely extract error message from API response
// Handles FastAPI validation errors which can be arrays of objects
const extractErrorMessage = (error) => {
  const detail = error?.response?.data?.detail;
  if (!detail) return error?.message || 'Something went wrong';
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    return detail.map(e => e.msg || e.message || JSON.stringify(e)).join(', ');
  }
  if (typeof detail === 'object') {
    return detail.msg || detail.message || JSON.stringify(detail);
  }
  return 'Something went wrong';
};

// Error message component with different severity levels
export const ErrorMessage = ({
  error,
  title = 'An error occurred',
  onRetry = null,
  severity = 'error' // 'error', 'warning', 'info'
}) => {
  const severityStyles = {
    error: {
      bg: 'bg-red-50',
      border: 'border-red-200',
      text: 'text-red-800',
      icon: <XCircleIcon size={20} className="text-red-600" />
    },
    warning: {
      bg: 'bg-yellow-50',
      border: 'border-yellow-200',
      text: 'text-yellow-800',
      icon: <ExclamationTriangleIcon size={20} className="text-yellow-600" />
    },
    info: {
      bg: 'bg-teal-50',
      border: 'border-teal-200',
      text: 'text-teal-800',
      icon: <ExclamationTriangleIcon size={20} className="text-teal-600" />
    }
  };

  const style = severityStyles[severity];
  const errorMessage = extractErrorMessage(error);

  return (
    <div className={`${style.bg} ${style.border} border rounded-lg p-4`}>
      <div className="flex items-start">
        <div className="flex-shrink-0">{style.icon}</div>
        <div className="ml-3 flex-1">
          <h3 className={`text-sm font-medium ${style.text}`}>{title}</h3>
          <div className={`mt-2 text-sm ${style.text}`}>
            <p>{errorMessage}</p>
          </div>
          {onRetry && (
            <button
              onClick={onRetry}
              className="mt-3 text-sm font-medium text-teal-600 hover:text-teal-500"
            >
              Try again →
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

// Full page error component
export const FullPageError = ({
  error,
  onRetry = null,
  onGoHome = null
}) => {
  const errorCode = error?.response?.status || 500;
  const errorMessage = extractErrorMessage(error);

  return (
    <div className="min-h-screen flex items-center justify-center bg-stone-50">
      <div className="max-w-md w-full text-center">
        <div className="mb-8">
          <XCircleIcon size={96} className="mx-auto text-red-400" />
        </div>
        <h1 className="text-6xl font-bold text-stone-900 mb-4">{errorCode}</h1>
        <p className="text-xl font-semibold text-stone-700 mb-2">Oops! Something went wrong</p>
        <p className="text-stone-600 mb-8">{errorMessage}</p>
        <div className="flex justify-center space-x-4">
          {onRetry && (
            <button
              onClick={onRetry}
              className="px-6 py-3 bg-teal-600 text-white rounded-lg hover:bg-teal-700 flex items-center"
            >
              <ArrowPathIcon size={20} className="mr-2" />
              Try Again
            </button>
          )}
          {onGoHome && (
            <button
              onClick={onGoHome}
              className="px-6 py-3 bg-stone-200 text-stone-800 rounded-lg hover:bg-stone-300 flex items-center"
            >
              <HomeIcon size={20} className="mr-2" />
              Go Home
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

// Network error component
export const NetworkError = ({ onRetry }) => (
  <div className="min-h-screen flex items-center justify-center bg-stone-50">
    <div className="max-w-md w-full text-center">
      <div className="mb-8">
        <WifiIcon size={96} className="mx-auto text-stone-400" />
      </div>
      <h2 className="text-2xl font-bold text-stone-900 mb-4">No Internet Connection</h2>
      <p className="text-stone-600 mb-8">
        Please check your internet connection and try again.
      </p>
      <button
        onClick={onRetry}
        className="px-6 py-3 bg-teal-600 text-white rounded-lg hover:bg-teal-700 flex items-center mx-auto"
      >
        <ArrowPathIcon size={20} className="mr-2" />
        Retry
      </button>
    </div>
  </div>
);

// Empty state component
export const EmptyState = ({
  title = 'No data found',
  message = 'There are no items to display.',
  icon = null,
  action = null
}) => (
  <div className="text-center py-12">
    {icon && <div className="mb-4">{icon}</div>}
    <h3 className="text-lg font-medium text-stone-900 mb-2">{title}</h3>
    <p className="text-stone-600 mb-6">{message}</p>
    {action}
  </div>
);

// Inline error component
export const InlineError = ({ error, className = '' }) => {
  const errorMessage = extractErrorMessage(error);

  return (
    <div className={`text-sm text-red-600 ${className}`}>
      <p>⚠️ {errorMessage}</p>
    </div>
  );
};

// Form field error component
export const FieldError = ({ error }) => {
  if (!error) return null;

  return (
    <p className="mt-1 text-sm text-red-600">
      {error.message || error}
    </p>
  );
};

// Toast error notification
export const showErrorToast = (error, options = {}) => {
  const message = extractErrorMessage(error);

  // This assumes you're using react-toastify
  // Import and use toast from react-toastify in your app
  if (window.toast) {
    window.toast.error(message, {
      position: "top-right",
      autoClose: 5000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
      ...options
    });
  } else {
    // Fallback to alert if toast is not available
    alert(message);
  }
};

// API error handler
export const handleApiError = (error) => {
  if (error.response) {
    // Server responded with error status
    switch (error.response.status) {
      case 401:
        // Unauthorized - redirect to login
        localStorage.removeItem('access_token');
        localStorage.removeItem('user');
        window.location.href = '/login';
        break;
      case 403:
        // Forbidden
        return {
          title: 'Access Denied',
          message: 'You do not have permission to perform this action.'
        };
      case 404:
        // Not found
        return {
          title: 'Not Found',
          message: 'The requested resource was not found.'
        };
      case 422:
        // Validation error
        return {
          title: 'Validation Error',
          message: error.response.data.detail || 'Please check your input and try again.'
        };
      case 500:
        // Server error
        return {
          title: 'Server Error',
          message: 'An internal server error occurred. Please try again later.'
        };
      default:
        return {
          title: 'Error',
          message: error.response.data.detail || 'An unexpected error occurred.'
        };
    }
  } else if (error.request) {
    // Request made but no response
    return {
      title: 'Network Error',
      message: 'Unable to connect to the server. Please check your internet connection.'
    };
  } else {
    // Something else happened
    return {
      title: 'Error',
      message: error.message || 'An unexpected error occurred.'
    };
  }
};

// Retry mechanism with exponential backoff
export const retryWithBackoff = async (fn, retries = 3, delay = 1000) => {
  try {
    return await fn();
  } catch (error) {
    if (retries === 0) throw error;

    await new Promise(resolve => setTimeout(resolve, delay));
    return retryWithBackoff(fn, retries - 1, delay * 2);
  }
};

// Error boundary fallback component
export const ErrorBoundaryFallback = ({ error, resetErrorBoundary }) => {
  const { t } = useTranslation('common');
  return (
  <div className="min-h-screen flex items-center justify-center bg-stone-50 p-4">
    <div className="max-w-md w-full bg-white rounded-xl border border-stone-200 shadow-lg p-8">
      <div className="text-center">
        <div className="w-16 h-16 bg-red-50 rounded-full flex items-center justify-center mx-auto mb-5">
          <XCircleIcon size={32} className="text-red-500" />
        </div>
        <h2 className="text-xl font-bold text-stone-900 mb-2">{t('error.boundary.title')}</h2>
        <p className="text-sm text-stone-500 mb-6">
          {error?.message || t('error.boundary.message')}
        </p>
        <div className="flex justify-center gap-3">
          <button
            onClick={resetErrorBoundary}
            className="flex items-center gap-2 px-5 py-2 bg-teal-600 text-white text-sm font-semibold rounded-lg hover:bg-teal-700 transition-colors"
          >
            <ArrowPathIcon size={16} />
            {t('action.retry')}
          </button>
          <button
            onClick={() => window.location.href = '/'}
            className="flex items-center gap-2 px-5 py-2 bg-stone-100 text-stone-700 text-sm font-semibold rounded-lg hover:bg-stone-200 transition-colors"
          >
            <HomeIcon size={16} />
            {t('action.goHome')}
          </button>
        </div>
        {import.meta.env.DEV && error?.stack && (
          <details className="mt-6 text-left">
            <summary className="cursor-pointer text-xs text-stone-400 hover:text-stone-600">{t('error.boundary.devDetails')}</summary>
            <pre className="mt-2 text-xs bg-stone-50 border border-stone-100 p-3 rounded-lg overflow-auto max-h-48 text-left">
              {error.stack}
            </pre>
          </details>
        )}
      </div>
    </div>
  </div>
  );
};

export default {
  ErrorMessage,
  FullPageError,
  NetworkError,
  EmptyState,
  InlineError,
  FieldError,
  showErrorToast,
  handleApiError,
  retryWithBackoff,
  ErrorBoundaryFallback
};